import { Action } from 'redux';
import * as actions from './actions';
import { LoadingAction } from '../common/loading-action';
import { ErrorAction } from '../common/error-action';
import { NewCollaborator } from '@services/collaborator/types/new-collaborator';
import { Collaborator } from '@services/collaborator/types/collaborator';
import { ArtistSearchResult } from '@services/artist/types/artist-search-result';

export class CreateCollaborator implements LoadingAction {
  readonly type = actions.CREATE_COLLABORATOR;
  readonly isLoading = true;

  constructor(
    public collaborator: NewCollaborator,
    public isReplicatingFromFirstTrack?: boolean
  ) {}
}

export class CreateArtistCollaborator implements LoadingAction {
  readonly type = actions.CREATE_ARTIST_COLLABORATOR;
  readonly isLoading = true;

  constructor(
    public newCollaborator: NewCollaborator,
    public name: string,
    public externalArtist?: ArtistSearchResult,
    public isReplicatingFromFirstTrack?: boolean
  ) {}
}

export class CreateCollaboratorSuccess implements LoadingAction {
  readonly type = actions.CREATE_COLLABORATOR_SUCCESS;
  readonly isLoading = false;

  constructor(
    public collaborator: Collaborator,
    public isReplicatingFromFirstTrack?: boolean
  ) {}
}

export class CreateCollaboratorError implements ErrorAction {
  readonly type = actions.CREATE_COLLABORATOR_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateCollaborator implements LoadingAction {
  readonly type = actions.UPDATE_COLLABORATOR;
  readonly isLoading = true;

  constructor(public collaborator: Collaborator) {}
}

export class UpdateCollaboratorSuccess implements LoadingAction {
  readonly type = actions.UPDATE_COLLABORATOR_SUCCESS;
  readonly isLoading = false;

  constructor(
    public collaborator: Collaborator,
    public oldCollaboratorId?: string
  ) {}
}

export class UpdateCollaboratorError implements ErrorAction {
  readonly type = actions.UPDATE_COLLABORATOR_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateCollaboratorOrder implements LoadingAction {
  readonly type = actions.UPDATE_COLLABORATOR_ORDER;
  readonly isLoading = true;

  constructor(public assetId: string, public newOrder: string[]) {}
}

export class UpdateCollaboratorOrderSuccess implements LoadingAction {
  readonly type = actions.UPDATE_COLLABORATOR_ORDER_SUCCESS;
  readonly isLoading = false;

  constructor(public assetId: string, public newOrder: string[]) {}
}

export class UpdateCollaboratorOrderError implements ErrorAction {
  readonly type = actions.UPDATE_COLLABORATOR_ORDER_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class GetCollaborators implements LoadingAction {
  readonly type = actions.GET_COLLABORATORS;
  readonly isLoading = true;

  constructor(public assetId: string) {}
}

export class GetCollaboratorsSuccess implements LoadingAction {
  readonly type = actions.GET_COLLABORATORS_SUCCESS;
  readonly isLoading = false;

  constructor(public collaborators: Collaborator[]) {}
}

export class GetCollaboratorsError implements ErrorAction {
  readonly type = actions.GET_COLLABORATORS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateCollaboratorField<T extends keyof Collaborator>
  implements Action
{
  readonly type = actions.UPDATE_COLLABORATOR_FIELD;

  constructor(
    public assetId: string,
    public collaboratorId: string,
    public field: T,
    public value: Collaborator[T]
  ) {}
}

export class RemoveCollaborator implements LoadingAction {
  readonly type = actions.REMOVE_COLLABORATOR;
  readonly isLoading = true;

  constructor(public assetId: string, public collaboratorId: string) {}
}

export class RemoveCollaboratorSuccess implements LoadingAction {
  readonly type = actions.REMOVE_COLLABORATOR_SUCCESS;
  readonly isLoading = false;

  constructor(public assetId: string, public collaboratorId: string) {}
}

export class RemoveCollaboratorError implements ErrorAction {
  readonly type = actions.REMOVE_COLLABORATOR_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class ClearCollaboratorState implements Action {
  readonly type = actions.CLEAR_COLLABORATOR_STATE;
}

export type CollaboratorActions =
  | CreateCollaborator
  | CreateArtistCollaborator
  | CreateCollaboratorSuccess
  | CreateCollaboratorError
  | GetCollaborators
  | GetCollaboratorsSuccess
  | GetCollaboratorsError
  | UpdateCollaboratorField<keyof Collaborator>
  | UpdateCollaborator
  | UpdateCollaboratorSuccess
  | UpdateCollaboratorError
  | UpdateCollaboratorOrder
  | UpdateCollaboratorOrderSuccess
  | UpdateCollaboratorOrderError
  | RemoveCollaborator
  | RemoveCollaboratorSuccess
  | RemoveCollaboratorError
  | ClearCollaboratorState;
